import {
  MULTIPLE_VALUES_INDICATOR,
  processFormValues,
  type SkuQualificationGroupModel,
  type SkuQualificationRow,
} from "helpers/skuQualificationHelpers";
import {
  ISkuQualificationPreviewResponse,
  ISkuQualificationRequest,
  IValidateCourseNumberResponse,
  SkuQualificationSubjectAreaRequest,
} from "services/KnowfullyAdminClient";
import { ANY_PROFESSION_ID } from "constants/skuQualificationConstants";
import { toUtcMidnight } from "utils/dateUtils";
import type { CreditLine } from "./CreditsSlot";
import { NO_LINK_VALUE, type ReferenceRow } from "./qualificationLinkState";

export const withAcronym = (name?: string, acronym?: string) =>
  acronym ? `${name} (${acronym})` : (name ?? "-");

// The wizard writes one SkuQualification per (entry x profession), so its form holds every
// request field except the two the fan-out supplies.
export type WizardFormValues = Omit<
  ISkuQualificationRequest,
  "qualificationId" | "professionId" | "id" | "duplicateHandling" | "subjectAreas"
> & {
  professionIds: string[];
  // The editable rows behind subjectAreas. They carry a React key the request has no room
  // for, so they are mapped on save. Left undefined the save does not touch the stored
  // splits; an empty array clears them.
  creditLines?: CreditLine[];
  // With entries under several accreditors, each accreditor's own rows, keyed by its id:
  // an accreditor only takes the Subject Areas it recognizes.
  creditLinesByAccreditor?: Record<string, CreditLine[]>;
};

export const emptyWizardFormValues = (): WizardFormValues => ({
  professionIds: [],
  stateIds: [],
  courseNumber: "",
  approvalCode: "",
  notes: "",
});

// An Accreditation Body is a Qualification, so the entry's accreditationBodyId is the
// QualificationId. Profession and state sentinels stay in processFormValues so the wizard
// resolves them exactly like the form it replaces.
// How a save identifies what it replaces. A group's own id is synthetic, so its members travel
// as childSkuQualificationIds and the save merges field by field. A single row travels as its
// own id, the only path where clearing a field actually clears it.
export type EditTarget = {
  skuQualificationId?: string;
  childSkuQualificationIds?: string[];
};

// Every fanned member of a group keeps the id of the SkuQualification it came from, so a group
// drawn from one SkuQualification shares its stored credit lines and can show them.
export const creditLinesSourceId = (target?: EditTarget): string | undefined => {
  if (target?.skuQualificationId) return target.skuQualificationId;
  const ids = new Set(target?.childSkuQualificationIds);
  return ids.size === 1 ? Array.from(ids)[0] : undefined;
};

// A row the author never gave a Subject Area to is not a split, so it is dropped rather than
// sent with an empty id. Undefined in, undefined out: the save leaves stored splits alone.
export const toSubjectAreaRequests = (
  lines?: CreditLine[],
): SkuQualificationSubjectAreaRequest[] | undefined =>
  lines
    ?.filter((line) => !!line.subjectAreaId)
    .map((line) =>
      SkuQualificationSubjectAreaRequest.fromJS({
        id: line.id,
        subjectAreaId: line.subjectAreaId,
        credits: line.credits,
        overrides: line.overrides
          ?.filter((override) => !!override.subjectAreaId)
          .map((override) => ({
            licensureBodyId: override.licensureBodyId,
            subjectAreaId: override.subjectAreaId,
            // No amount follows the line's.
            credits: override.credits,
          })),
      }),
    );

const sameCredits = (a: number, b: number) => Math.abs(a - b) < 0.005;

// A line with no Subject Area is not sent, so it counts for nothing.
const creditsOf = (line: CreditLine) => (line.subjectAreaId ? Number(line.credits) || 0 : 0);

export const sharedTotal = (lines: CreditLine[]): number =>
  lines.reduce((sum, line) => sum + creditsOf(line), 0);

// A line the board does not override counts with its shared credits.
export const boardTotal = (lines: CreditLine[], licensureBodyId: string): number =>
  lines.reduce((sum, line) => {
    const own = line.subjectAreaId
      ? line.overrides?.find((override) => override.licensureBodyId === licensureBodyId)
      : undefined;
    return sum + (own?.credits ?? creditsOf(line));
  }, 0);

export const boardIsValid = (lines: CreditLine[], licensureBodyId: string): boolean =>
  lines.every((line) =>
    (line.overrides ?? []).every(
      (override) =>
        override.licensureBodyId !== licensureBodyId ||
        override.credits === undefined ||
        override.credits > 0,
    ),
  ) && sameCredits(boardTotal(lines, licensureBodyId), sharedTotal(lines));

export const overridesAreValid = (lines?: CreditLine[]): boolean =>
  Array.from(boardsWithOverrides(lines)).every((id) => boardIsValid(lines ?? [], id));

// A line moved to another Subject Area, or removed, is a different line, so what boards counted it as goes.
// A board area is one line's, so the area a line takes also stops being another line's board area.
export const dropStaleOverrides = (
  before: CreditLine[],
  after: CreditLine[],
): { lines: CreditLine[]; lost: string[] } => {
  const areaBefore = (line: CreditLine) =>
    before.find((candidate) => candidate.key === line.key)?.subjectAreaId;
  const areaAfter = (line: CreditLine) =>
    after.find((candidate) => candidate.key === line.key)?.subjectAreaId;
  const moved = before.filter((line) => areaAfter(line) !== line.subjectAreaId);
  const isMoved = (line: CreditLine) => moved.some((candidate) => candidate.key === line.key);
  const claimed = new Set(
    after
      .filter((line) => line.subjectAreaId && areaBefore(line) !== line.subjectAreaId)
      .map((line) => line.subjectAreaId),
  );
  const conflicting = (line: CreditLine) =>
    (line.overrides ?? []).filter((override) => claimed.has(override.subjectAreaId));
  const kept = after.filter((line) => !isMoved(line));
  const lost = [...moved.flatMap((line) => line.overrides ?? []), ...kept.flatMap(conflicting)];

  return {
    lines: after.map((line) => {
      if (isMoved(line)) return { ...line, overrides: undefined };
      if (conflicting(line).length === 0) return line;
      return {
        ...line,
        overrides: line.overrides?.filter((override) => !claimed.has(override.subjectAreaId)),
      };
    }),
    lost: Array.from(new Set(lost.map((override) => override.licensureBodyId))),
  };
};

// An Add never shows the overrides stored on a record it overrides, so each line it sends carries
// the board overrides on screen, none included, instead of keeping the stored ones.
export const withShownOverrides = (values: WizardFormValues): WizardFormValues => {
  const shown = (lines: CreditLine[]) =>
    lines.map((line) => ({ ...line, overrides: line.overrides ?? [] }));

  return {
    ...values,
    creditLines: values.creditLines && shown(values.creditLines),
    creditLinesByAccreditor:
      values.creditLinesByAccreditor &&
      Object.fromEntries(
        Object.entries(values.creditLinesByAccreditor).map(([id, lines]) => [id, shown(lines)]),
      ),
  };
};

// The boards whose overrides tie the qualification to its one profession.
export const boardsWithOverrides = (lines?: CreditLine[]): Set<string> =>
  new Set(
    (lines ?? []).flatMap((line) => (line.overrides ?? []).map((override) => override.licensureBodyId)),
  );

export const toSkuQualificationRequests = (
  entries: ReferenceRow[],
  values: WizardFormValues,
  editTarget?: EditTarget,
): ISkuQualificationRequest[] => {
  const mixedAccreditors = new Set(entries.map((entry) => entry.accreditationBodyId)).size > 1;
  const linesFor = (accreditorId: string) =>
    mixedAccreditors ? values.creditLinesByAccreditor?.[accreditorId] : values.creditLines;

  const requests = entries.flatMap((entry, entryIndex) =>
    values.professionIds.map((selectedProfessionId, professionIndex) => {
      const { professionId, stateIds, useQualificationApprovals } =
        processFormValues(selectedProfessionId, values.stateIds);

      // Only the first record can claim the edited row; the rest are professions being added.
      // A record that expands server side into several rows claims nothing, since one id cannot
      // identify many rows: that is every approved profession, or more than one state.
      const claimsEditedRow =
        entryIndex === 0 &&
        professionIndex === 0 &&
        !useQualificationApprovals &&
        (stateIds?.length ?? 0) <= 1;

      return {
        id: claimsEditedRow ? editTarget?.skuQualificationId : undefined,
        qualificationId: entry.accreditationBodyId,
        professionId,
        stateIds,
        useQualificationApprovals,
        courseNumber: values.courseNumber,
        approvalCode: values.approvalCode,
        approvalDate: toUtcMidnight(values.approvalDate) ?? undefined,
        releaseDate: toUtcMidnight(values.releaseDate) ?? undefined,
        creditSubmissionExpirationDate:
          toUtcMidnight(values.creditSubmissionExpirationDate) ?? undefined,
        credits: values.credits,
        minimumCredit: values.minimumCredit,
        partialCreditIncrement: values.partialCreditIncrement,
        notes: values.notes,
        childSkuQualificationIds: editTarget?.childSkuQualificationIds,
        subjectAreas: toSubjectAreaRequests(linesFor(entry.accreditationBodyId)),
      };
    }),
  );

  // The licensure body is not persisted, so entries sharing an Accreditation Body resolve to
  // the same record: one request each, not one per entry.
  return distinctByQualificationAndProfession(requests);
};

// Each gate below runs once per request and the dialogs take a single response, so the
// responses are folded into that same shape instead of changing the dialogs.
export const mergeCourseNumberValidations = (
  responses: IValidateCourseNumberResponse[],
): IValidateCourseNumberResponse => ({
  hasConflict: responses.some((response) => response.hasConflict),
  existingSkuCourseNumbers: responses.flatMap(
    (response) => response.existingSkuCourseNumbers ?? [],
  ),
});

export const mergePreviews = (
  responses: ISkuQualificationPreviewResponse[],
): ISkuQualificationPreviewResponse => ({
  existingQualifications: responses.flatMap(
    (response) => response.existingQualifications ?? [],
  ),
  totalNew: responses.reduce(
    (total, response) => total + (response.totalNew ?? 0),
    0,
  ),
  totalExisting: responses.reduce(
    (total, response) => total + (response.totalExisting ?? 0),
    0,
  ),
});

// A grouped row aggregates its members, so a field they disagree on reads as the multiple
// values marker. Editing must leave those blank rather than write the marker back.
const cleanValue = <T,>(
  value: T | typeof MULTIPLE_VALUES_INDICATOR,
): T | undefined =>
  value === MULTIPLE_VALUES_INDICATOR ? undefined : (value as T);

const toEntry = (row: SkuQualificationRow): ReferenceRow => ({
  lb: withAcronym(row.licensureBodyName, row.licensureBodyAcronym),
  ab: withAcronym(row.accreditationBodyName, row.accreditationBodyAcronym),
  sa: row.submissionAuthorityName ?? NO_LINK_VALUE,
  licensureBodyId: row.licensureBodyId ?? "",
  accreditationBodyId: row.accreditationBodyId ?? row.qualification?.id ?? "",
});

export type WizardEditState = {
  entries: ReferenceRow[];
  initialValues: WizardFormValues;
  editTarget: EditTarget;
};

// An Accreditation Body is a Qualification, so a row with no resolved chain still knows its
// AB through the qualification it carries.
export const toWizardEditState = (
  row: SkuQualificationGroupModel,
): WizardEditState => {
  const isGroup = row.isGroup === true;
  const children = isGroup ? row.childQualifications : [];

  return {
    // A group row carries aggregated values, so reading the chain off it yields the
    // MULTIPLE_VALUES indicator instead of a body name. The members hold the real chain.
    entries: children.length > 0 ? children.map(toEntry) : [toEntry(row)],
    editTarget: isGroup
      ? {
          childSkuQualificationIds: children
            .map((child) => child.id)
            .filter((id): id is string => !!id),
        }
      : { skuQualificationId: row.id },
    initialValues: {
      // A row saved with the Any profession stores none; the sentinel resolves back on save.
      professionIds: [row.profession?.id || ANY_PROFESSION_ID],
      stateIds: isGroup
        ? children
            .map((child) => child.stateId)
            .filter((id): id is string => !!id)
        : row.stateId
          ? [row.stateId]
          : [],
      courseNumber: cleanValue(row.courseNumber),
      approvalCode: cleanValue(row.approvalCode),
      approvalDate: cleanValue(row.approvalDate),
      releaseDate: cleanValue(row.releaseDate),
      creditSubmissionExpirationDate: cleanValue(
        row.creditSubmissionExpirationDate,
      ),
      credits: cleanValue(row.credits),
      minimumCredit: cleanValue(row.minimumCredit),
      partialCreditIncrement: cleanValue(row.partialCreditIncrement),
      notes: cleanValue(row.notes),
    },
  };
};

// Requests sharing a (qualification, profession) resolve to the same record server side, so
// keeping the first avoids issuing an identical call for every one of them.
export const distinctByQualificationAndProfession = (
  requests: ISkuQualificationRequest[],
): ISkuQualificationRequest[] => {
  const seen = new Set<string>();

  return requests.filter((request) => {
    const key = `${request.qualificationId}|${request.professionId ?? ""}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};
